/* sessionStorage can throw (blocked site data); the switcher must still work without it */
const safeStorage = {
  get(key) {
    try { return sessionStorage.getItem(key); } catch (e) { return null; }
  },
  set(key, value) {
    try { sessionStorage.setItem(key, value); } catch (e) {}
  },
};

class StoreSwitcher extends HTMLElement {
  constructor() {
    super();
    this.storeConfig = this.querySelector('#Store-switcher-config') && JSON.parse(document.querySelector('#Store-switcher-config').innerHTML);
    this.currentCountry = this.storeConfig.current_store.iso_code;
    this.availableStores = this.storeConfig.available_stores;
    this.currentLocation = new URL(window.location.href);

    this.COUNTRYDRAWER = document.querySelector('dra-wer#Modal-storeswitcher');
    this.changeModeBtns = this.querySelectorAll('button[data-set-mode]');

    this.CONFIG = {
      storageNameForLocationPreference: "wasAskedForCountrySwitch",
      storageNameForVisitorCountry: "visitorCountry",
      searchParamForRedirectedUser: "wasRedirected", /* e.g. ?[param] = US */
    };
  }

  get userLocationPreference() {
    return safeStorage.get(this.CONFIG.storageNameForLocationPreference);
  }

  set userLocationPreference(countryLocationIsoCode) {
    safeStorage.set(this.CONFIG.storageNameForLocationPreference, countryLocationIsoCode);
  }

  get userIsFromRedirect() {
    return this.currentLocation.searchParams.has(this.CONFIG.searchParamForRedirectedUser);
  }

  get userHasLocationPreference() {
    return !!this.userLocationPreference;
  }


  get shouldAutoRedirect() {
    /* theme setting "activate_utm_redirect" switches the automatic redirect off */
    if (!this.storeConfig.current_store.utm_redirect) return false;

    if (!this.userIsFromRedirect) {
      let specialParamValues = ["QR", "insta-fb-abc", "linktree"];

      if (this.currentLocation.searchParams.has('utm_source')) {
        if (specialParamValues.indexOf(this.currentLocation.searchParams.get('utm_source')) > -1) {
          return true;
        }
      }
    }

    return false;
  }

  promptUserToSwitchCountry(recommendationMode, recommendation_iso) {
    /* open only when the recommended country has a button to show */
    if (recommendationMode && !this.querySelector('store-switcher-btn[iso-code="' + recommendation_iso + '"]')) return false;

    this.COUNTRYDRAWER.openDrawer();

    if (recommendationMode) {
      this._setMode('recommendation', recommendation_iso);
    }
    return true;
  }

  autoRedirect() {
    this.redirectTo(this.USER_COUNTRY_CODE, true);
  }

  async checkUserSession() {
    /* never in the theme editor */
    if (window.Shopify?.designMode) return;

    if (this.userIsFromRedirect) {
     this.userLocationPreference = 'wasRedirected';
     return;
    }

    /* no reason to popup if the user already has a preference.
       QR/social links still redirect, unless this tab already arrived here through a store redirect */
    const preference = this.userLocationPreference;
    if (preference && (!this.shouldAutoRedirect || preference === 'wasRedirected')) {
      return;
    }

    await this.getUserLocation();

    /* the visitor picked a country while we were looking it up: their choice wins */
    if (this.manualChoiceMade) return;

    /* unknown country: do nothing and do not remember it, so a later visit can still redirect/prompt */
    if (!this.USER_COUNTRY_CODE) {
      return;
    }

    /* if user is already on the matching currentCountry, do nothing */
    if (this.currentCountry === this.USER_COUNTRY_CODE) {
      return;
    }

    if (this.shouldAutoRedirect) {
      this.autoRedirect();
      return;
    }

    /* current situation:
    - user has no preference
    - we should not redirect automatically
    - was not redirected
    - AND their country didn't match the @currentStore
    */

    /* now: Check if we have a better suited country than the existing...."); */
    if (this.currentStoreHasCountry || this.otherStoresHasCountry) {
      if (this.promptUserToSwitchCountry(true, this.USER_COUNTRY_CODE)) this.userLocationPreference = 'wasPrompted';
    }
  }

  get currentStoreHasCountry() {
    return this.storeConfig.current_store.countries_available_on_store.indexOf(this.USER_COUNTRY_CODE) > -1;
  }

  get otherStoresHasCountry() {
    return !!this.availableStores[this.USER_COUNTRY_CODE];
  }

  /* Visitor country from Shopify's own geolocation (same origin, no third-party quota), looked up once per visit.
     ipapi.co's free tier answered 429 without CORS headers, which silently disabled every redirect and prompt. */
  async getUserLocation() {
    const isCountryCode = (value) => typeof value === 'string' && /^[A-Z]{2}$/.test(value);

    const cached = safeStorage.get(this.CONFIG.storageNameForVisitorCountry);
    if (isCountryCode(cached)) {
      this.USER_COUNTRY_CODE = cached;
      return;
    }

    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}browsing_context_suggestions.json?country[enabled]=true`);
      if (!res.ok) return;
      const resJSON = await res.json();
      const country = resJSON?.detected_values?.country?.handle;
      if (isCountryCode(country)) {
        this.USER_COUNTRY_CODE = country;
        safeStorage.set(this.CONFIG.storageNameForVisitorCountry, country);
      }
    } catch (e) {}
  }

  /* automatic redirects replace the history entry, so Back does not bounce the visitor again */
  _navigate(url, automatic) {
    if (automatic) {
      window.location.replace(url);
    } else {
      window.location.href = url;
    }
  }

  redirectTo(isoCode, automatic = false) {
    if (this.storeConfig.current_store.countries_available_on_store.indexOf(isoCode) > -1) {
      /* already asked for this country once: never reload the same URL again */
      if (this.currentLocation.searchParams.get('country') === isoCode) return;

      this.currentLocation.searchParams.set("country", isoCode);
      this._navigate(this.currentLocation.href, automatic);
      return;
    }

    /* we have nothing to do or recommend here... :) */
    if (!this.availableStores[isoCode]) {
      return;
    }

    const store = this.availableStores[isoCode];
    const MATCHING_HREFLANG = (store.region_w_langcode || '').toLowerCase();

    /* the store's own homepage is the fallback, and its host is the only allowed destination for this country */
    let rootURL;
    try { rootURL = new URL(store.root_url); } catch (e) { return; }

    /* look in hreflang tags in DOM */
    const hreflangLink = [...document.querySelectorAll('link[rel="alternate"][hreflang]')]
      .find((link) => link.getAttribute('hreflang').toLowerCase() === MATCHING_HREFLANG);

    let redirectURLObject = rootURL;
    if (hreflangLink) {
      try {
        const candidate = new URL(hreflangLink.getAttribute('href'), window.location.href);
        const bareHost = (host) => host.replace(/^www\./, '');
        if (candidate.protocol === 'https:' && bareHost(candidate.host) === bareHost(rootURL.host)) redirectURLObject = candidate;
      } catch (e) {}
    }

    redirectURLObject.searchParams.set(this.CONFIG.searchParamForRedirectedUser, "true");
    redirectURLObject.searchParams.set('country', isoCode);

    /* keep the campaign parameters (e.g. utm_source=QR) so the scan is attributed in the country store */
    this.currentLocation.searchParams.forEach((value, key) => {
      if (key.startsWith('utm_') && !redirectURLObject.searchParams.has(key)) redirectURLObject.searchParams.set(key, value);
    });

    this._navigate(redirectURLObject.href, automatic);
  }

  listenForUserInteractions() {
    this._listenForChangeModeInteractions();
  }

  _setMode(modeStr, iso_code) {
    this.setAttribute('data-mode', modeStr);

    if (iso_code) {
      const button = this.querySelector('store-switcher-btn[iso-code="' + iso_code +'"]');
      if (button) this.querySelector('[data-recommended-country]').appendChild(button.cloneNode(true));
    }
  }

  _listenForChangeModeInteractions() {
    this.changeModeBtns.forEach((elem) => {
      elem.addEventListener('click', () => {
        this._setMode(elem.getAttribute('data-set-mode'));
      });
    });
  }

  connectedCallback() {
    this.listenForUserInteractions();
    this.checkUserSession();

    this.addEventListener('store-switcher:redirect', e => {
      this.manualChoiceMade = true;
      this.redirectTo(e.detail.iso_code);
    });
  }
}

class StoreSwitcherBtn extends HTMLElement {
  constructor() {
    super();

    this.associatedStoreSwitcher = this.closest('store-switcher');
  }

  get isoCode() {
    return this.getAttribute('iso-code');
  }

  sendRedirectEvent() {
    this.dispatchEvent(new CustomEvent('store-switcher:redirect', {
      bubbles: true,
      detail: {
        iso_code: this.isoCode,
      },
    }));
  }

  connectedCallback() {
    this.addEventListener('click', this.sendRedirectEvent.bind(this));
  }
}

customElements.define('store-switcher', StoreSwitcher);
customElements.define('store-switcher-btn', StoreSwitcherBtn);
